/**
 * List command - lists all sessions for the user
 *
 * Shows sessions from the local registry (~/.spacecode/sessions.json).
 * Falls back to the backend API if available.
 */

import chalk from 'chalk';
import { loadSessions, activeSessions, pruneSessions, type SessionRecord } from '../sessions.js';

function formatAge(date: Date): string {
  const ms = Date.now() - date.getTime();
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}

function statusColor(status: string): (s: string) => string {
  switch (status) {
    case 'running': return chalk.green;
    case 'stopped': return chalk.yellow;
    case 'destroyed': return chalk.dim;
    default: return chalk.white;
  }
}

function formatSessionRow(s: SessionRecord): string {
  const id = s.id.padEnd(10);
  const name = (s.name ?? '-').padEnd(20);
  const status = statusColor(s.status)(s.status.toUpperCase().padEnd(10));
  const repo = (s.repo ? s.repo.replace('https://github.com/', '') : '-').padEnd(30);
  const age = formatAge(new Date(s.createdAt)).padEnd(10);
  return `  ${id} ${name} ${status} ${repo} ${age}`;
}

export interface ListOptions {
  all?: boolean;
}

export async function list(options: ListOptions = {}): Promise<void> {
  console.log(chalk.bold('\nSessions\n'));

  // Auto-prune old destroyed sessions -- best-effort, listing must still work if this fails
  await pruneSessions(24).catch((err) => {
    console.warn(chalk.dim(`Warning: Could not prune old sessions: ${err instanceof Error ? err.message : String(err)}`));
  });

  const sessions = await loadSessions();

  if (sessions.length === 0) {
    console.log(chalk.dim('No sessions found.'));
    console.log(chalk.dim('\nRun `spacecode launch` to create one.'));
    return;
  }

  const display = options.all ? sessions : activeSessions(sessions);

  if (display.length === 0) {
    console.log(chalk.dim('No active sessions.'));
    console.log(chalk.dim('\nRun `spacecode launch` to create one.'));
    console.log(chalk.dim('Run `spacecode list --all` to show destroyed sessions.'));
    return;
  }

  // Header
  const header = `  ${'ID'.padEnd(10)} ${'NAME'.padEnd(20)} ${'STATUS'.padEnd(10)} ${'REPO'.padEnd(30)} ${'AGE'.padEnd(10)}`;
  console.log(chalk.dim(header));
  console.log(chalk.dim('  ' + '\u2500'.repeat(82)));

  for (const session of display) {
    console.log(formatSessionRow(session));
  }

  // Summary
  const running = display.filter(s => s.status === 'running').length;
  const stopped = display.filter(s => s.status === 'stopped').length;
  console.log();
  console.log(chalk.dim(`Running: ${running}  Stopped: ${stopped}  Total: ${display.length}`));
}
